import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { pricingAgreementsApi } from '../../api/endpoints.js';
import type { PricingAgreementSummary } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  PageHeader,
  Panel,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RecordLink } from '../clients/record-layout.js';

/**
 * Pricing agreements.
 *
 * "In force today" is COMPUTED BY THE BACKEND against the laboratory's own
 * calendar day and returned as a fact. Recomputing it here from the two dates
 * would be a second implementation of the rule, and the browser's idea of
 * today is not the laboratory's.
 *
 * A withdrawn agreement stays listed and marked. Hiding it would make a
 * laboratory's own history look shorter than it is, and the quotations written
 * under it still refer to it.
 */
export function PricingAgreementsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [page, setPage] = useState(1);

  const agreements = useQuery({
    queryKey: ['pricing-agreements', { page }],
    queryFn: () => pricingAgreementsApi.list({ page }),
  });

  const columns: readonly DataColumn<PricingAgreementSummary>[] = [
    {
      key: 'client',
      header: t('pricingAgreements.client'),
      render: (agreement) => (
        // The client opens the agreement: the row's way in (D278).
        <span className="font-semibold">
          <RecordLink to={`/pricing-agreements/${agreement.id}`}>
            {agreement.clientName ?? agreement.clientNumber}
          </RecordLink>
        </span>
      ),
    },
    {
      key: 'title',
      header: t('pricingAgreements.titleEn'),
      render: (agreement) =>
        agreement.title ? (
          <span className="text-default-800" dir="auto">
            {agreement.title}
          </span>
        ) : (
          <span className="text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'validFrom',
      header: t('pricingAgreements.validFrom'),
      render: (agreement) => (
        <span className="text-default-600">
          {formatDate(agreement.validFrom, { locale })}
        </span>
      ),
    },
    {
      key: 'validUntil',
      header: t('pricingAgreements.validUntil'),
      render: (agreement) =>
        agreement.validUntil ? (
          <span className="text-default-600">
            {formatDate(agreement.validUntil, { locale })}
          </span>
        ) : (
          <span className="text-default-600">{t('common.none')}</span>
        ),
    },
    {
      key: 'lineCount',
      header: t('pricingAgreements.lineCount'),
      align: 'end',
      render: (agreement) => (
        <span className="measure text-default-800" dir="ltr">
          {agreement.lineCount}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (agreement) => (
        // The backend's own answer, rendered rather than recomputed.
        <StatusBadge
          label={
            !agreement.isActive
              ? t('pricingAgreements.withdrawn')
              : agreement.isEffectiveToday
                ? t('pricingAgreements.effectiveToday')
                : t('pricingAgreements.notEffectiveToday')
          }
          tone={
            !agreement.isActive
              ? 'neutral'
              : agreement.isEffectiveToday
                ? 'success'
                : 'warning'
          }
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (agreement) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/pricing-agreements/${agreement.id}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('pricingAgreements.title')}
        subtitle={t('pricingAgreements.subtitle')}
        breadcrumbs={[{ label: t('nav.pricingAgreements') }]}
        actions={
          can(PERMISSIONS.PRICING_AGREEMENTS_MANAGE) ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate('/pricing-agreements/new')}
            >
              {t('pricingAgreements.create')}
            </ActionButton>
          ) : null
        }
      />

      <Panel>
        <DataTable
          label={t('pricingAgreements.title')}
          columns={columns}
          rows={agreements.data?.data ?? []}
          rowKey={(agreement) => agreement.id}
          isLoading={agreements.isLoading}
          error={agreements.error}
          onRetry={() => void agreements.refetch()}
          emptyContent={t('pricingAgreements.noneYet')}
          page={page}
          totalPages={agreements.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
